import React, { useId } from "react";
import { motion, useReducedMotion, type Variants } from "framer-motion";
import { ArrowUpRightIcon } from "lucide-react";
import { TreatedImage } from "./TreatedImage";
import { NudgeArrow } from "./NudgeArrow";
import { CurrentlyWorkingOn } from "./CurrentlyWorkingOn";
import { BODY, DISPLAY_HEADING, EYEBROW, PANEL_HEADING } from "../utils/typography";
const ease = [0.23, 1, 0.32, 1] as const;
const PORTRAIT_URL = "https://cdn.magicpatterns.com/uploads/vocf1y2AQQYxjkG8kUjPXn/face.jpg";
const RESUME_URL = "https://cdn.magicpatterns.com/uploads/5EmUHiPAb2KjzbrHBdvvKz/Alyssa-Retza-Resume.pdf";
const leadWords = ["Progress,", "with"];
const highlightWord = "humanity";
const trailWord = "intact.";
const WORD_STAGGER = 0.06;
const WORD_START = 0.15;
// Paint begins once every word, including "intact.", has risen into place
const PAINT_DELAY = WORD_START + (leadWords.length + 2) * WORD_STAGGER + 0.25;
// Arrow lands as the paint finishes, matching the section headings
const ARROW_DELAY = PAINT_DELAY + 0.45;
const lineVariants: Variants = {
  hidden: {},
  shown: {
    transition: {
      staggerChildren: WORD_STAGGER,
      delayChildren: WORD_START
    }
  }
};
const wordVariants: Variants = {
  hidden: {
    y: "105%"
  },
  shown: {
    y: "0%",
    transition: {
      duration: 0.3,
      ease
    }
  }
};
const brushEase = [0.33, 0.9, 0.4, 1] as const;
export function Hero() {
  const reduce = useReducedMotion();
  const brushId = `brush-${useId().replace(/:/g, "")}`;
  return <section aria-labelledby="hero-heading" className="w-full" data-id="element-252" data-mp="358rcygz62489">
      <div className="bg-cream px-6 pb-24 pt-36 md:px-12 md:pb-32 md:pt-52 lg:px-20 lg:pb-40" data-id="element-253" data-mp="35aat87ktofa1">
        <div className="flex items-end gap-4 md:gap-[2vw]" data-id="element-254" data-mp="2qleoaje2bidn">
          {/* Arrow — same placement and motion as the PROJECTS / THOUGHTS headings */}
          <motion.span aria-hidden="true" className="block shrink-0 pb-[0.6vw]" initial={reduce ? false : {
          opacity: 0,
          x: -12,
          y: -12
        }} animate={{
          opacity: 1,
          x: 0,
          y: 0
        }} transition={{
          duration: 0.3,
          ease,
          delay: ARROW_DELAY
        }} data-id="element-255" data-mp="2hjf6vpz4yca6">
            <NudgeArrow delay={ARROW_DELAY + 0.4} data-id="element-256" data-mp="nbl0ygmt488c" />
          </motion.span>

          <motion.h1 id="hero-heading" aria-label="Progress, with humanity intact." className={`max-w-[16ch] ${DISPLAY_HEADING} text-espresso`} initial={reduce ? false : "hidden"} animate="shown" variants={lineVariants} data-id="element-257" data-mp="n9i3jbjbrctl">
            <span aria-hidden="true" data-id="element-258" data-mp="161t24t6y4olw">
              {leadWords.map(word => <span key={word} className="-my-[0.08em] inline-block overflow-hidden py-[0.08em] align-bottom" data-id="element-259" data-mp="15zq1lf863d2x">
                  <motion.span variants={wordVariants} className="inline-block" data-id="element-260" data-mp="x14o3yhjyo90">
                    {word}
                  </motion.span>
                  <span className="inline-block" data-id="element-261" data-mp="3lu04j5ufbd1u">&nbsp;</span>
                </span>)}

              {/* Extra side padding so the brush edges aren't clipped by the rise mask */}
              <span className="-mx-[0.18em] -my-[0.08em] inline-block overflow-hidden px-[0.18em] py-[0.08em] align-bottom" data-id="element-262" data-mp="1xa431cdmkgik">
                <motion.span variants={wordVariants} className="relative isolate inline-block whitespace-nowrap" data-id="element-263" data-mp="21bfu7mc5d9sv">
                  {/* Painted highlight behind "humanity" */}
                  <svg className="absolute -left-[0.12em] top-[0.2em] -z-10 h-[0.82em] w-[calc(100%+0.24em)] overflow-visible" viewBox="0 0 400 100" preserveAspectRatio="none" data-id="element-264" data-mp="2b1rbeakudfhp">
                    <defs data-id="element-265" data-mp="2l0q76skidgna">
                      <filter id={brushId} x="-5%" y="-30%" width="110%" height="160%" data-id="element-266" data-mp="2ozz60bueo4pp">
                        <feTurbulence type="fractalNoise" baseFrequency="0.04 0.25" numOctaves="2" seed="3" data-id="element-267" data-mp="2tzbokke8upmh" />
                        <feDisplacementMap in="SourceGraphic" scale="9" xChannelSelector="R" yChannelSelector="G" data-id="element-268" data-mp="umzkk23cggai" />
                      </filter>
                    </defs>
                    <g filter={`url(#${brushId})`} data-id="element-269" data-mp="4bdxli20sdpo">
                      <motion.path d="M14 56 C 80 44, 160 62, 240 50 S 360 46, 388 52" fill="none" stroke="#e2a33a" strokeWidth="70" strokeLinecap="round" initial={reduce ? false : {
                      pathLength: 0
                    }} animate={{
                      pathLength: 1
                    }} transition={{
                      duration: 0.6,
                      ease: brushEase,
                      delay: PAINT_DELAY
                    }} data-id="element-270" data-mp="4a2tfh4nx3y1" />
                      <motion.path d="M30 26 C 120 20, 220 32, 300 24 S 370 26, 380 30" fill="none" stroke="#e2a33a" strokeWidth="12" strokeLinecap="round" opacity="0.7" initial={reduce ? false : {
                      pathLength: 0
                    }} animate={{
                      pathLength: 1
                    }} transition={{
                      duration: 0.5,
                      ease: brushEase,
                      delay: PAINT_DELAY + 0.08
                    }} data-id="element-271" data-mp="3csqstj2czs6e" />
                      <motion.path d="M24 86 C 110 92, 210 80, 290 88 S 360 84, 376 80" fill="none" stroke="#e2a33a" strokeWidth="10" strokeLinecap="round" opacity="0.6" initial={reduce ? false : {
                      pathLength: 0
                    }} animate={{
                      pathLength: 1
                    }} transition={{
                      duration: 0.5,
                      ease: brushEase,
                      delay: PAINT_DELAY + 0.12
                    }} data-id="element-272" data-mp="1e9vvx8pt2wi2" />
                    </g>
                  </svg>
                  {highlightWord}
                </motion.span>
              </span>

              <span className="-my-[0.08em] inline-block overflow-hidden py-[0.08em] align-bottom" data-id="element-273" data-mp="7tjripwe4ie1">
                <span className="inline-block" data-id="element-274" data-mp="bun4n4n200bk">&nbsp;</span>
                <motion.span variants={wordVariants} className="inline-block" data-id="element-275" data-mp="bw7zywh6znah">
                  {trailWord}
                </motion.span>
              </span>
            </span>
          </motion.h1>
        </div>

        <CurrentlyWorkingOn delay={ARROW_DELAY + 0.1} data-id="element-276" data-mp="29ktq7x8t0he1" />

        {/* Intro — who I am, what I do, résumé + contact */}
        {/* Intro: the portrait sits on the cream page as its own print and
            overlaps the left edge of the separate espresso box beside it */}
        <article aria-labelledby="intro-heading" className="mt-24 grid grid-cols-1 md:mt-32 lg:mx-auto lg:mt-40 lg:w-[88%] lg:max-w-[1280px] lg:grid-cols-12 lg:items-center" data-id="element-277" data-mp="1vsnkz676vutd">
          <motion.div className="relative z-10 -mb-20 mx-auto w-[70%] max-w-[18rem] -rotate-2 rounded-3xl shadow-[0_24px_48px_-16px_rgba(15,12,10,0.45)] md:-mb-24 md:max-w-xs lg:col-span-5 lg:col-start-1 lg:row-start-1 lg:mb-0 lg:ml-0 lg:w-full lg:max-w-none" initial={reduce ? false : {
          opacity: 0,
          y: 16,
          rotate: -4
        }} animate={{
          opacity: 1,
          y: 0,
          rotate: -2
        }} transition={{
          duration: 0.3,
          ease,
          delay: ARROW_DELAY
        }} data-id="element-278" data-mp="1lt54izqhmr62">
            <TreatedImage src={PORTRAIT_URL} alt="Alyssa Retza" className="aspect-[4/5] w-full rounded-3xl bg-bark" data-id="element-279" data-mp="3np46n7dmlt0x" />
          </motion.div>

          <motion.div className="rounded-3xl bg-espresso px-8 pb-10 pt-28 md:px-14 md:pb-16 md:pt-36 lg:col-span-8 lg:col-start-5 lg:row-start-1 lg:py-20 lg:pl-32 lg:pr-20 xl:pl-36" initial={reduce ? false : {
          opacity: 0,
          y: 24,
          scale: 0.98
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} transition={{
          duration: 0.3,
          ease,
          delay: ARROW_DELAY - 0.15
        }} data-id="element-280" data-mp="19j5wa26q4xf">
            <motion.h2 id="intro-heading" aria-label="Hi, I’m Alyssa." className={`${PANEL_HEADING} text-cream`} initial={reduce ? false : "hidden"} animate="shown" variants={{
            hidden: {},
            shown: {
              transition: {
                staggerChildren: 0.1,
                delayChildren: ARROW_DELAY
              }
            }
          }} data-id="element-281" data-mp="3erbs45fqapel">
              <span aria-hidden="true" className="-my-[0.06em] block overflow-hidden py-[0.06em]" data-id="element-282" data-mp="1b3thr4ghzzim">
                <motion.span className="block" variants={{
                hidden: {
                  y: "110%"
                },
                shown: {
                  y: "0%",
                  transition: {
                    duration: 0.3,
                    ease
                  }
                }
              }} data-id="element-283" data-mp="1ouomc4jyruge">
                  Hi, I’m Alyssa<span className="text-mustard" data-id="element-284" data-mp="1trf18ok18k08">.</span>
                </motion.span>
              </span>
            </motion.h2>

            <dl className="mt-12 space-y-8 md:mt-14 md:space-y-10" data-id="element-285" data-mp="27rg8clqw4no5">
              {[{
              label: "Currently",
              value: "Director of Digital Strategy & Web Product, Lawrence University"
            }, {
              label: "What I do",
              value: "I make complex digital systems make more sense, while asking what all this progress actually means for the people living with it."
            }].map(row => <div key={row.label} data-id="element-286" data-mp="2p1250eylitn1">
                  <dt className={`${EYEBROW} text-mustard`} data-id="element-287" data-mp="2ba5b8ksxo6ee">{row.label}</dt>
                  <dd className={`mt-2 max-w-2xl ${BODY} text-cream`} data-id="element-288" data-mp="2b82picytczfh">{row.value}</dd>
                </div>)}
            </dl>

            <div className="mt-12 flex flex-wrap gap-3 md:mt-14" data-id="element-289" data-mp="21bg33jqua0bc">
              <a href={RESUME_URL} target="_blank" rel="noopener noreferrer" aria-label="View my résumé (PDF, opens in a new tab)" className="flex h-12 items-center gap-3 whitespace-nowrap rounded-full bg-rust px-6 text-sm font-medium uppercase text-cream transition-colors duration-150 ease-out hover:bg-mustard hover:text-espresso" data-id="element-290" data-mp="21ec3rfu2n33h">
                View my résumé
                <ArrowUpRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-291" data-mp="3qpmde7q369tj" />
              </a>
              <a href="#contact" className="flex h-12 items-center whitespace-nowrap rounded-full px-6 text-sm font-medium uppercase text-cream ring-1 ring-cream/20 transition-colors duration-150 ease-out hover:bg-cream hover:text-espresso" data-id="element-292" data-mp="b2tzjp4yybki">
                Let’s talk
              </a>
            </div>
          </motion.div>
        </article>
      </div>
    </section>;
}